import { Contract, Repetition, Unit, WorkingTimeClassicType } from "../../__generated__/graphql";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useFormatWorkingTimeClassicSpec, useI18n } from "../../hooks/i18n-hooks";
import VStack from "../../ui_atoms/VStack";

import SelectField from "../SelectField";
import {
    MAX_PHYSICAL_DAILY_HOURS,
    maxDaysPerWeekForRepetition,
    useHasErrorWorkingTimeClassicSpec,
    WorkingTimeComponentProps,
} from "./helpers";
import { BodyMediumText, BodySmallText } from "../FormattedText";
import Gap from "../../ui_atoms/Gap";
import { Buildable } from "ts-essentials";
import { useTheme } from "../../hooks/use-theme";
import { SelectItemType } from "../select/SelectChooser";
import { MAX_HOURS_PER_WEEK } from "../../utils";
import OptionsField from "../OptionsField";
import QuantityField from "../QuantityField";
import { useContractCct } from "../../hooks/cct-utils";

const DailyClassicComponent: React.FC<WorkingTimeComponentProps> = ({
    disabled = false,
    contract,
    setContract,
}) => {
    const { colors } = useTheme();
    const i18n = useI18n();
    const workingTime = contract.workingTimeClassicSpec;
    const repetition = contract.workingTimeClassicSpec?.repetition;
    const [isRepetition, setIsRepetition] = useState(repetition !== Repetition.None);
    const unitCount = workingTime?.unitCount || undefined;
    const dailyHours = workingTime?.dailyHours || undefined;
    const setter = useCallback(
        (v: { workingTime: Buildable<WorkingTimeClassicType> }) =>
            setContract({
                ...contract,
                workingTimeClassicSpec: v.workingTime,
            }),
        [setContract, contract],
    );
    const missionCct = useContractCct(contract as Contract);

    const hasErrorWorkingTimeClassicSpec = useHasErrorWorkingTimeClassicSpec();

    const { errorUnitCount, errorDailyHours } = useMemo(() => {
        const hoursPerWeek = missionCct?.hoursPerWeek || MAX_HOURS_PER_WEEK;
        return hasErrorWorkingTimeClassicSpec(
            contract.workingTimeClassicSpec?.unit || Unit.Hours,
            repetition,
            Number(unitCount),
            hoursPerWeek,
            Number(dailyHours),
        );
    }, [
        hasErrorWorkingTimeClassicSpec,
        missionCct?.hoursPerWeek,
        repetition,
        unitCount,
        dailyHours,
        contract.workingTimeClassicSpec?.unit,
    ]);

    const changeWorkingTime = useCallback(
        (_unitCount?: number, _dailyHours?: number, repetition?: Repetition) => {
            if (
                _unitCount !== undefined &&
                (isNaN(_unitCount) ||
                    _unitCount <= 0 ||
                    (repetition && _unitCount > maxDaysPerWeekForRepetition(repetition)))
            ) {
                _unitCount = undefined;
            }
            if (
                _dailyHours !== undefined &&
                (isNaN(_dailyHours) || _dailyHours <= 0 || _dailyHours > MAX_PHYSICAL_DAILY_HOURS)
            ) {
                _dailyHours = undefined;
            }
            setter({
                workingTime: {
                    repetition,
                    unit: Unit.Days,
                    unitCount: _unitCount,
                    dailyHours: _dailyHours,
                },
            });
        },
        [setter],
    );

    const onRepetitionChange = useCallback(
        (value: boolean) => {
            setIsRepetition(value);
            if (!value) {
                changeWorkingTime(unitCount, dailyHours, Repetition.None);
            } else {
                changeWorkingTime(unitCount, dailyHours, undefined);
            }
        },
        [changeWorkingTime, dailyHours, unitCount],
    );

    const onUnitCountChange = useCallback(
        (value?: number) => {
            changeWorkingTime(value, dailyHours, repetition);
        },
        [repetition, dailyHours, changeWorkingTime],
    );
    const onDailyHoursChange = useCallback(
        (value?: number) => {
            changeWorkingTime(unitCount, value, repetition);
        },
        [repetition, unitCount, changeWorkingTime],
    );
    const onRepetitionChanges = useCallback(
        ({ value }: SelectItemType<Repetition>) => {
            changeWorkingTime(unitCount, dailyHours, value);
        },
        [unitCount, dailyHours, changeWorkingTime],
    );

    useEffect(() => {
        if (!contract.workingTimeClassicSpec) {
            setContract({
                ...contract,
                workingTimeClassicSpec: {
                    repetition: Repetition.Week,
                    unitCount: undefined,
                    unit: Unit.Days,
                },
            });
        }
    }, [contract, setContract]);

    const repetitionItems = useMemo(() => {
        return [
            {
                label: i18n.t("utils.working_time.repetition_for_daily.week"),
                value: Repetition.Week,
            },
            {
                label: i18n.t("utils.working_time.repetition_for_daily.week2"),
                value: Repetition.Week2,
            },
            {
                label: i18n.t("utils.working_time.repetition_for_daily.week3"),
                value: Repetition.Week3,
            },
            {
                label: i18n.t("utils.working_time.repetition_for_daily.week4"),
                value: Repetition.Week4,
            },
            {
                label: i18n.t("utils.working_time.repetition_for_daily.month"),
                value: Repetition.Month,
            },
        ];
    }, [i18n]);
    const formatWorkingTime = useFormatWorkingTimeClassicSpec();

    if (!contract.workingTimeClassicSpec) {
        return null;
    }
    return (
        <VStack v="1/3" gap={0.5} style={{ paddingTop: 0 }}>
            <OptionsField<boolean>
                value={isRepetition}
                onSelect={onRepetitionChange}
                data={[
                    { label: i18n.t("utils.working_time.recurrent"), value: true },
                    { label: i18n.t("utils.working_time.punctual"), value: false },
                ]}
            />
            {isRepetition && (
                <>
                    <Gap gap={0.5} />
                    <BodyMediumText>
                        {i18n.t("utils.working_time.repetition_info_daily")}
                    </BodyMediumText>
                    <SelectField
                        onSelect={onRepetitionChanges}
                        placeholder={i18n.t("utils.working_time.repetition_label_daily")}
                        data={repetitionItems}
                        value={repetition || undefined}
                    />
                </>
            )}
            <Gap gap={0.1} />
            <BodyMediumText>
                {i18n.t(`utils.working_time.days_info.${repetition || Repetition.None}`)}
            </BodyMediumText>
            <QuantityField
                selectTextOnFocus
                decimalPlaces={0}
                style={{ alignSelf: "stretch" }}
                disabled={disabled}
                value={unitCount}
                onChange={onUnitCountChange}
                error={unitCount ? errorUnitCount : undefined}
            />
            {workingTime?.unitCount && workingTime?.repetition && (
                <BodySmallText color={colors.fieldLabel}>
                    {formatWorkingTime(workingTime)}
                </BodySmallText>
            )}
            <Gap gap={0.1} />
            <BodyMediumText>{i18n.t("utils.working_time.daily_hours_question")}</BodyMediumText>
            <QuantityField
                selectTextOnFocus
                decimalPlaces={2}
                style={{ alignSelf: "stretch" }}
                disabled={disabled}
                value={dailyHours}
                onChange={onDailyHoursChange}
                error={dailyHours ? errorDailyHours : undefined}
            />
            {workingTime?.dailyHours && (
                <BodySmallText color={colors.fieldLabel}>
                    {i18n.t("utils.working_time.daily_hours", {
                        count: workingTime?.dailyHours,
                    })}
                </BodySmallText>
            )}
        </VStack>
    );
};

export default DailyClassicComponent;
